import { Node, mergeAttributes } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/core";
import { useState } from "react";
import { PencilIcon, TrashIcon } from "lucide-react";
import { blockMetadata } from "@/app/Blogs/Components/blockMetadata";
import { BlockInsertDialog } from "@/app/Blogs/Components/BlockInsertDialog";

export type CustomBlockAttrs = {
  name: string;
  props: string;
};

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    customBlock: {
      insertCustomBlock: (attrs: CustomBlockAttrs) => ReturnType;
    };
  }
}

export const CustomBlock = Node.create({
  name: "customBlock",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,

  addAttributes() {
    return {
      name: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-block") ?? "",
        renderHTML: (attrs) => ({ "data-block": attrs.name }),
      },
      props: {
        default: "{}",
        parseHTML: (el) => el.getAttribute("data-props") ?? "{}",
        renderHTML: (attrs) => ({ "data-props": attrs.props }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "div[data-block]" }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(CustomBlockView);
  },

  addCommands() {
    return {
      insertCustomBlock:
        (attrs) =>
        ({ chain }) =>
          chain()
            .focus()
            .insertContent({
              type: this.name,
              attrs,
            })
            .run(),
    };
  },
});

function CustomBlockView({
  node,
  updateAttributes,
  deleteNode,
}: NodeViewProps) {
  const { name, props } = node.attrs as CustomBlockAttrs;
  const [editOpen, setEditOpen] = useState(false);
  const meta = blockMetadata[name] ?? {
    label: name || "Unknown block",
    description: "",
  };

  let preview: Record<string, unknown> = {};
  try {
    preview = JSON.parse(props || "{}") as Record<string, unknown>;
  } catch {
    /* leave empty */
  }

  const canEdit = !!blockMetadata[name];

  return (
    <NodeViewWrapper
      as="div"
      data-block={name}
      data-drag-handle
      className="my-4 rounded-md border-2 border-dashed border-zinc-300 bg-zinc-50 px-4 py-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
          {meta.label}
        </span>
        <div className="flex items-center gap-1">
          {canEdit ? (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="rounded p-1 text-zinc-500 hover:bg-zinc-200 hover:text-zinc-900"
              aria-label="Edit block"
            >
              <PencilIcon className="size-4" />
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => deleteNode()}
            className="rounded p-1 text-zinc-500 hover:bg-red-100 hover:text-red-700"
            aria-label="Delete block"
          >
            <TrashIcon className="size-4" />
          </button>
        </div>
      </div>
      {meta.description ? (
        <p className="mt-1 text-sm text-zinc-600">{meta.description}</p>
      ) : null}
      {Object.keys(preview).length > 0 ? (
        <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 text-xs text-zinc-600">
          {Object.entries(preview)
            .filter(([, v]) => v !== null && v !== "")
            .slice(0, 6)
            .map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="font-medium">{k}</dt>
                <dd className="truncate font-mono">{stringify(v)}</dd>
              </div>
            ))}
        </dl>
      ) : null}
      {canEdit ? (
        <BlockInsertDialog
          open={editOpen}
          onClose={setEditOpen}
          editing={{ name, props: preview }}
          onInsert={(_name, nextProps) => {
            updateAttributes({ props: JSON.stringify(nextProps) });
          }}
        />
      ) : null}
    </NodeViewWrapper>
  );
}

function stringify(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
